import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DataGridPro,
  GridCellParams,
  GridColDef,
  GridRowSelectionModel,
  GridToolbarContainer,
  GridToolbarQuickFilter,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { LicenseInfo } from "@mui/x-license-pro";
import { Alert, Box, Button, Chip, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import LinearProgress, { linearProgressClasses } from "@mui/material/LinearProgress";
import { styled } from "@mui/material/styles";
import { useDispatch } from "react-redux";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { EFB_BULK_DEVICE_LIMIT, IEFBBulkDeviceResult, IEFBSummaryUser } from "@vmosolutions/oc-interfaces";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { useCancellablePromise } from "src/API/useCancellablePromise";
import LoadingWrapper from "src/components/LoadingWrapper/LoadingWrapper";
import { openSnackbar } from "src/redux/actions/App.Actions";
import { IActiveModal } from "src/pages/Admin/AdminWindow";
import { DeviceBulkActionBar } from "./DeviceBulkActionBar";
import { StaleDeviceFilter } from "./StaleDeviceFilter";
import {
  applyCrewSelection,
  chunk,
  describeDeviceForConfirm,
  describeSelection,
  describeThreshold,
  DeviceGridRow,
  buildFlatRows,
  buildGroupedRows,
  deviceRowId,
  foldBulkChunk,
  formatLastActive,
  groupDevicesByCrew,
  IStaleThreshold,
  isCrewGroupRow,
  lastActiveSortValue,
  pluralise,
  staleQueryString,
} from "./deviceGridHelpers";

LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_KEY as string);

type DeviceState = "activate" | "deactivate";

/** API paths for one device surface (crew devices, or non-crew user devices). */
export interface IDeviceGridEndpoints {
  list: string;
  invite: (userId: string) => string;
  device: (deviceId: string) => string;
  setState: (state: DeviceState, deviceId: string) => string;
  bulkRemove: string;
  bulkSetState: (state: DeviceState) => string;
}

export interface IDeviceDataGridProps {
  setActiveModal: (activeModal: IActiveModal | null) => void;
  endpoints: IDeviceGridEndpoints;
  /** Lower-case noun for the record owner, e.g. "crew member" or "user". */
  memberNoun: string;
  /** Header for the owner column. */
  memberColumnHeader: string;
  /**
   * Multiplier applied to `state.batteryLevel` to get a percentage: 1 when the
   * reading is already a percentage, 100 when it is a 0-1 fraction.
   *
   * The two tabs disagree today and this preserves each one's existing
   * behaviour rather than silently changing what an admin sees.
   */
  batteryScale: 1 | 100;
  /** Battery bar turns amber below this percentage. Omit for a single colour. */
  batteryWarnBelow?: number;
  emptyMessage: string;
}

const BorderLinearProgress = styled(LinearProgress, {
  shouldForwardProp: (prop) => prop !== "warn",
})<{ warn?: boolean }>(({ theme, warn }) => ({
  height: 10,
  borderRadius: 1,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[400],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 1,
    backgroundColor: warn ? theme.palette.warning.dark : theme.palette.primary.dark,
  },
}));

/** Search box above the grid - the list runs to hundreds of crew members. */
const DeviceGridToolbar = (props: { placeholder?: string }): JSX.Element => (
  <GridToolbarContainer>
    <GridToolbarQuickFilter debounceMs={300} placeholder={props.placeholder} sx={{ width: 380 }} />
  </GridToolbarContainer>
);

/**
 * Shared Crew Devices / User Devices admin grid.
 *
 * Adds the ENG-602 cleanup tooling - a sortable Last Active column, a
 * server-side staleness filter, grouping by owner, row selection and bulk
 * deactivate/remove - once, for both tabs. Previously the two grids were
 * duplicated line for line, so anything built into only one of them would have
 * left the sibling tab visibly inconsistent.
 */
export const DeviceDataGrid = (props: IDeviceDataGridProps): JSX.Element => {
  const { setActiveModal, endpoints, memberNoun, memberColumnHeader, batteryScale, batteryWarnBelow, emptyMessage } =
    props;

  const { cancellablePromise } = useCancellablePromise();
  const dispatch = useDispatch();
  // Needed to drive the group expand/collapse toggle from a custom grouping cell.
  const apiRef = useGridApiRef();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [devices, setDevices] = useState<IEFBSummaryUser[]>([]);
  const [threshold, setThreshold] = useState<IStaleThreshold | null>(null);
  const [grouped, setGrouped] = useState<boolean>(false);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [busy, setBusy] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const notify = useCallback(
    (variant: "info" | "success" | "error", message: string) => {
      dispatch(openSnackbar({ open: true, closeDelay: variant === "error" ? null : 4000, variant, message }));
    },
    [dispatch]
  );

  const loadDevices = useCallback(
    async (staleFilter: IStaleThreshold | null): Promise<void> => {
      setIsLoading(true);
      const response = await AmplifyAPIWrapper.Get<IEFBSummaryUser[]>(
        `${endpoints.list}${staleQueryString(staleFilter)}`,
        undefined,
        cancellablePromise
      );
      setIsLoading(false);
      if (Array.isArray(response)) {
        setDevices(response);
        setSelectionModel([]);
        setLoadError(null);
        return;
      }
      // The API wrapper resolves to null on a failed request. Without this the
      // previous list stays on screen and a failed filter reads as a clean
      // result - actively misleading right before a destructive action.
      setDevices([]);
      setSelectionModel([]);
      setLoadError("Could not load the device list. The request did not complete.");
    },
    // `cancellablePromise` is a fresh closure on every render, so depending on
    // it would re-create this callback each render and re-fire the load effect
    // in a loop. It only ever registers an unmount canceller, so a stale
    // reference is harmless.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [endpoints.list]
  );

  useEffect(() => {
    void loadDevices(null);
  }, [loadDevices]);

  const handleFind = useCallback(
    (next: IStaleThreshold | null) => {
      setThreshold(next);
      void loadDevices(next);
    },
    [loadDevices]
  );

  const groups = useMemo(() => groupDevicesByCrew(devices), [devices]);

  const rows = useMemo<DeviceGridRow[]>(
    () => (grouped ? buildGroupedRows(groups) : buildFlatRows(devices)),
    [grouped, groups, devices]
  );

  const rowsById = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows]);

  /** Only device rows carry actions; crew group rows are containers. */
  const selectedDevices = useMemo<IEFBSummaryUser[]>(() => {
    const out: IEFBSummaryUser[] = [];
    for (const id of selectionModel) {
      const row = rowsById.get(String(id));
      if (row && !isCrewGroupRow(row)) out.push(row);
    }
    return out;
  }, [selectionModel, rowsById]);

  /**
   * Checking a crew group row selects every device beneath it.
   *
   * The installed DataGrid Pro (7.11.1) has no `rowSelectionPropagation`, so the
   * parent/child relationship is applied here by hand; without it, ticking a
   * collapsed crew row would select nothing actionable.
   */
  const handleSelectionChange = useCallback(
    (next: GridRowSelectionModel) => {
      setSelectionModel(applyCrewSelection(selectionModel.map(String), next.map(String), rowsById));
    },
    [selectionModel, rowsById]
  );

  const selectAllMatching = useCallback(() => {
    setSelectionModel(devices.map((d) => deviceRowId(d.device.deviceId)));
  }, [devices]);

  const getInvitationLink = useCallback(
    async (userId: string): Promise<boolean> => {
      const notifications: INotifications = {
        dispatch,
        infoMessage: "Creating Invitation",
        successMessage: "Invitation Link Copied to clipboard",
        errorMessage: "Error: Unable To generate invitation",
      };
      const response = await AmplifyAPIWrapper.Get<{ url: string; jwt: string }>(
        endpoints.invite(userId),
        notifications
      );
      if (response) {
        void navigator.clipboard.writeText(response.url + response.jwt);
        return true;
      }
      return false;
    },
    [dispatch, endpoints]
  );

  // ─────────────────────────── single-device actions ───────────────────────────

  const removeSingleDevice = useCallback(
    async (row: IEFBSummaryUser): Promise<void> => {
      const notifications: INotifications = {
        dispatch,
        infoMessage: "Removing Selected Device",
        successMessage: "Device Removed Successfully",
        errorMessage: "Error: Unable To Remove Device",
      };
      // notifications belongs in the third slot; passing it as `body` (as this
      // call previously did) posted it as the request payload and meant the
      // admin never saw a success or failure message.
      const response = await AmplifyAPIWrapper.Del<IEFBSummaryUser>(
        endpoints.device(row.device.deviceId),
        undefined,
        notifications
      );
      if (response) void loadDevices(threshold);
    },
    [dispatch, endpoints, loadDevices, threshold]
  );

  const handleRemoveDevice = useCallback(
    (row: IEFBSummaryUser): void => {
      if (!row) return;
      setActiveModal({
        title: "Remove Selected Device",
        message: "Are you certain you wish to remove this device? This operation cannot be undone.",
        list: [describeDeviceForConfirm(row)],
        destructive: true,
        cancel: () => setActiveModal(null),
        confirm: () => {
          setActiveModal(null);
          void removeSingleDevice(row);
        },
        confirmLabel: "Remove",
      });
    },
    [setActiveModal, removeSingleDevice]
  );

  const toggleDeviceState = useCallback(
    async (row: IEFBSummaryUser): Promise<void> => {
      const nextState: DeviceState = row.device?.active ? "deactivate" : "activate";
      const verb = row.device?.active ? "Deactivat" : "Activat";
      const notifications: INotifications = {
        dispatch,
        infoMessage: `${verb}ing Selected Device`,
        successMessage: `Device ${verb}ed Successfully`,
        errorMessage: `Error: Unable To ${verb}e Device`,
      };
      const response = await AmplifyAPIWrapper.Put<IEFBSummaryUser>(
        endpoints.setState(nextState, row.device.deviceId),
        {},
        notifications
      );
      if (response?.device?.deviceId) {
        setDevices((prev) => prev.map((d) => (d.device.deviceId === response.device.deviceId ? response : d)));
      }
    },
    [dispatch, endpoints]
  );

  const handleToggleDeviceState = useCallback(
    (row: IEFBSummaryUser): void => {
      if (!row) return;
      if (!row.device?.active) {
        void toggleDeviceState(row);
        return;
      }
      setActiveModal({
        title: "Deactivate Selected Device",
        message: "Please confirm that you wish to deactivate this device",
        list: [describeDeviceForConfirm(row)],
        cancel: () => setActiveModal(null),
        confirm: () => {
          setActiveModal(null);
          void toggleDeviceState(row);
        },
        confirmLabel: "Deactivate",
      });
    },
    [setActiveModal, toggleDeviceState]
  );

  // ───────────────────────────── bulk actions ─────────────────────────────

  /** Runs one bulk call per chunk and folds the partial results together. */
  const runBulk = useCallback(async (path: string, deviceIds: string[]): Promise<IEFBBulkDeviceResult> => {
    let total: IEFBBulkDeviceResult = { succeeded: [], failed: [], usersAffected: 0 };
    for (const batch of chunk(deviceIds, EFB_BULK_DEVICE_LIMIT)) {
      const response = await AmplifyAPIWrapper.Post<IEFBBulkDeviceResult>(path, { deviceIds: batch });
      total = foldBulkChunk(total, batch, response);
    }
    return total;
  }, []);

  const reportBulkOutcome = useCallback(
    (result: IEFBBulkDeviceResult, pastTense: string): void => {
      const ok = result.succeeded.length;
      const bad = result.failed.length;
      if (ok > 0 && bad === 0) notify("success", `${pluralise(ok, "device")} ${pastTense}.`);
      else if (ok > 0) notify("error", `${pluralise(ok, "device")} ${pastTense}; ${bad} could not be processed.`);
      else notify("error", `No devices were ${pastTense}. ${result.failed[0]?.reason ?? ""}`.trim());
    },
    [notify]
  );

  const performBulk = useCallback(
    async (kind: "remove" | DeviceState, rowsToAct: IEFBSummaryUser[]): Promise<void> => {
      const deviceIds = rowsToAct.map((r) => r.device.deviceId);
      if (deviceIds.length === 0) return;
      setBusy(true);
      try {
        if (kind === "remove") {
          reportBulkOutcome(await runBulk(endpoints.bulkRemove, deviceIds), "removed");
        } else {
          reportBulkOutcome(
            await runBulk(endpoints.bulkSetState(kind), deviceIds),
            kind === "activate" ? "activated" : "deactivated"
          );
        }
        await loadDevices(threshold);
      } finally {
        setBusy(false);
      }
    },
    [endpoints, loadDevices, reportBulkOutcome, runBulk, threshold]
  );

  const confirmBulkDeactivate = useCallback(
    (rowsToAct: IEFBSummaryUser[]): void => {
      if (rowsToAct.length === 0) return;
      setActiveModal({
        title: `Deactivate ${pluralise(rowsToAct.length, "device")}?`,
        message: `This will deactivate ${describeSelection(
          rowsToAct,
          memberNoun
        )}. Deactivation is reversible - the devices stay in the list and can be reactivated.`,
        list: rowsToAct.map(describeDeviceForConfirm),
        cancel: () => setActiveModal(null),
        confirm: () => {
          setActiveModal(null);
          void performBulk("deactivate", rowsToAct);
        },
        confirmLabel: `Deactivate ${rowsToAct.length}`,
      });
    },
    [setActiveModal, memberNoun, performBulk]
  );

  const confirmBulkRemove = useCallback(
    (rowsToAct: IEFBSummaryUser[]): void => {
      if (rowsToAct.length === 0) return;
      setActiveModal({
        title: `Remove ${pluralise(rowsToAct.length, "device")} permanently?`,
        message: `This will permanently remove ${describeSelection(
          rowsToAct,
          memberNoun
        )}. It cannot be undone. Use Deactivate instead if a ${memberNoun} may only be on leave.`,
        list: rowsToAct.map(describeDeviceForConfirm),
        destructive: true,
        // Mockup #3's escape hatch: a staleness filter cannot tell a departed
        // crew member from one on extended leave, so the reversible action is
        // offered right where the irreversible one is confirmed.
        secondary: {
          label: "Deactivate instead",
          action: () => {
            setActiveModal(null);
            void performBulk("deactivate", rowsToAct);
          },
        },
        cancel: () => setActiveModal(null),
        confirm: () => {
          setActiveModal(null);
          void performBulk("remove", rowsToAct);
        },
        confirmLabel: `Remove ${pluralise(rowsToAct.length, "device")}`,
      });
    },
    [setActiveModal, memberNoun, performBulk]
  );

  // ───────────────────────────── columns ─────────────────────────────

  const columns = useMemo<GridColDef[]>(() => {
    const ownerColumn: GridColDef = {
      flex: 35,
      field: "name",
      headerName: memberColumnHeader,
      type: "string",
      hideable: false,
      valueGetter: (_value, row: DeviceGridRow) => (isCrewGroupRow(row) ? "" : `${row.firstName} ${row.lastName}`),
    };

    const rest: GridColDef[] = [
      {
        flex: 15,
        field: "employeeId",
        headerName: "Emp. ID",
        type: "string",
        hideable: true,
        valueGetter: (_value, row: DeviceGridRow) =>
          isCrewGroupRow(row) ? row.group.employeeId : grouped ? "" : row.employeeId,
      },
      {
        flex: 55,
        field: "email",
        headerName: "Email",
        type: "string",
        hideable: true,
        valueGetter: (_value, row: DeviceGridRow) => (isCrewGroupRow(row) ? row.group.email : grouped ? "" : row.email),
      },
      {
        flex: 35,
        field: "device.model",
        headerName: "Device",
        type: "string",
        hideable: false,
        valueGetter: (_value, row: DeviceGridRow) =>
          isCrewGroupRow(row)
            ? ""
            : `${row.device?.model ?? ""} ${row.device?.systemName ?? ""} ${row.state?.systemVersion ?? ""}`.trim(),
        renderCell: (params: GridCellParams): JSX.Element => {
          const row = params.row as DeviceGridRow;
          if (isCrewGroupRow(row)) return <></>;
          return (
            <>
              {row.device?.model} ({row.device?.systemName} - {row.state?.systemVersion})
            </>
          );
        },
      },
      {
        flex: 20,
        field: "status",
        headerName: "Status",
        type: "string",
        hideable: false,
        valueGetter: (_value, row: DeviceGridRow) =>
          isCrewGroupRow(row) ? "" : row.device?.active ? "Active" : "Inactive",
      },
      {
        flex: 28,
        field: "lastActive",
        headerName: "Last Active",
        type: "number",
        hideable: false,
        valueGetter: (_value, row: DeviceGridRow) => (isCrewGroupRow(row) ? 0 : lastActiveSortValue(row)),
        renderCell: (params: GridCellParams): JSX.Element => {
          const row = params.row as DeviceGridRow;
          if (isCrewGroupRow(row)) return <></>;
          const label = formatLastActive(row);
          return (
            <Tooltip title={label.absolute}>
              <Box sx={{ lineHeight: 1.25, py: 0.25 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, fontStyle: label.inferred ? "italic" : "normal" }}>
                  {label.relative}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  {label.inferred ? "never reported" : label.absolute}
                </Typography>
              </Box>
            </Tooltip>
          );
        },
      },
      {
        flex: 25,
        field: "state.batteryLevel",
        headerName: "Battery",
        type: "number",
        hideable: false,
        valueGetter: (_value, row: DeviceGridRow) => (isCrewGroupRow(row) ? -1 : (row.state?.batteryLevel ?? -1)),
        renderCell: (params: GridCellParams): JSX.Element => {
          const row = params.row as DeviceGridRow;
          if (isCrewGroupRow(row)) return <></>;
          if (row.state?.batteryState === "unknown" || row.state?.batteryLevel === undefined) return <>Unknown</>;
          const percent = Math.max(0, Math.min(100, Math.round(row.state.batteryLevel * batteryScale)));
          return (
            <Grid container sx={{ display: "flex", alignItems: "center" }} columnSpacing={1}>
              <Grid item xs={7}>
                <BorderLinearProgress
                  variant="determinate"
                  value={percent}
                  warn={batteryWarnBelow !== undefined && percent < batteryWarnBelow}
                />
              </Grid>
              <Grid item xs={5}>
                {percent}%
              </Grid>
            </Grid>
          );
        },
      },
      {
        flex: 90,
        field: "state.availableGb",
        headerName: "Storage",
        type: "number",
        hideable: false,
        valueGetter: (_value, row: DeviceGridRow) => (isCrewGroupRow(row) ? -1 : (row.state?.availableGb ?? -1)),
        renderCell: (params: GridCellParams): JSX.Element => {
          const row = params.row as DeviceGridRow;
          if (isCrewGroupRow(row)) return <></>;
          const { capacityGb, availableGb } = row.state ?? {};
          if (!capacityGb || availableGb === null || availableGb === undefined) return <>Unknown</>;
          const percentUsed = ((capacityGb - availableGb) / capacityGb) * 100;
          return (
            <Grid container sx={{ display: "flex", alignItems: "center" }} columnSpacing={2}>
              <Grid item xs={6}>
                <BorderLinearProgress variant="determinate" value={percentUsed} />
              </Grid>
              <Grid item xs={6}>
                {availableGb}Gb Free {capacityGb}Gb Total
              </Grid>
            </Grid>
          );
        },
      },
      {
        flex: 30,
        field: "actions",
        headerName: "Actions",
        type: "string",
        hideable: false,
        sortable: false,
        renderCell: (params: GridCellParams): JSX.Element => {
          const row = params.row as DeviceGridRow;

          if (isCrewGroupRow(row)) {
            const stale = row.group.devices.filter((d) => !d.device?.active);
            if (stale.length === 0) return <></>;
            return (
              <Button
                variant="contained"
                color="error"
                size="small"
                disabled={busy}
                onClick={() => confirmBulkRemove(stale)}
              >
                Remove {stale.length} inactive
              </Button>
            );
          }

          return (
            <>
              <IconButton
                aria-label={row.device?.active ? "Deactivate" : "Activate"}
                size="medium"
                onClick={() => handleToggleDeviceState(row)}
              >
                {row.device?.active ? <ToggleOffIcon fontSize="inherit" /> : <ToggleOnIcon fontSize="inherit" />}
              </IconButton>
              <IconButton aria-label="Delete" size="small" onClick={() => handleRemoveDevice(row)}>
                <DeleteIcon fontSize="inherit" />
              </IconButton>
              <IconButton aria-label="Copy Invite Link" size="small" onClick={() => void getInvitationLink(row.userId)}>
                <ContentCopyIcon fontSize="inherit" />
              </IconButton>
            </>
          );
        },
      },
    ];

    // In grouped mode the tree's grouping column is the owner column.
    return grouped ? rest : [ownerColumn, ...rest];
  }, [
    memberColumnHeader,
    grouped,
    batteryScale,
    batteryWarnBelow,
    busy,
    confirmBulkRemove,
    handleToggleDeviceState,
    handleRemoveDevice,
    getInvitationLink,
  ]);

  /** Grouping column: crew name plus the total/inactive badges from mockup #2. */
  const groupingColDef = useMemo(
    () => ({
      headerName: memberColumnHeader,
      flex: 45,
      // The header cell carries a name, an employee id and two chips, so it
      // needs a floor - flex alone lets it squeeze until the badges clip.
      minWidth: 340,
      hideable: false,
      renderCell: (params: GridCellParams): JSX.Element => {
        const row = params.row as DeviceGridRow;
        if (!isCrewGroupRow(row)) return <></>;
        const { group } = row;
        // A custom renderCell replaces the grid's own grouping cell, and with it
        // the built-in expand chevron - so the toggle has to be rendered here or
        // the groups cannot be opened at all.
        const expanded = (params.rowNode as { childrenExpanded?: boolean })?.childrenExpanded === true;
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, overflow: "hidden" }}>
            <IconButton
              size="small"
              aria-label={
                expanded
                  ? `Collapse ${group.firstName} ${group.lastName}`
                  : `Expand ${group.firstName} ${group.lastName}`
              }
              onClick={(e) => {
                e.stopPropagation();
                apiRef.current.setRowChildrenExpansion(params.id, !expanded);
              }}
            >
              {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            </IconButton>
            <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
              {group.firstName} {group.lastName}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              {group.employeeId}
            </Typography>
            <Chip size="small" label={pluralise(group.total, "device")} />
            <Chip
              size="small"
              color={group.inactive > 0 ? "warning" : "default"}
              label={`${group.inactive} inactive`}
            />
          </Box>
        );
      },
    }),
    [memberColumnHeader, apiRef]
  );

  const resultSummary = useMemo(() => {
    const deviceLabel = pluralise(devices.length, "device");
    if (threshold) return `${deviceLabel} not active in ${describeThreshold(threshold)}`;
    if (grouped) return `${deviceLabel} across ${pluralise(groups.length, memberNoun)}`;
    return `${deviceLabel} total`;
  }, [devices.length, threshold, grouped, groups.length, memberNoun]);

  return (
    <>
      <StaleDeviceFilter
        onFind={handleFind}
        grouped={grouped}
        onGroupedChange={setGrouped}
        resultSummary={resultSummary}
        disabled={isLoading || busy}
        memberNoun={memberNoun}
      />

      <DeviceBulkActionBar
        selectedCount={selectedDevices.length}
        matchingCount={devices.length}
        allMatchingSelected={selectedDevices.length >= devices.length}
        onDeactivate={() => confirmBulkDeactivate(selectedDevices)}
        onRemove={() => confirmBulkRemove(selectedDevices)}
        onClear={() => setSelectionModel([])}
        onSelectAllMatching={selectAllMatching}
        busy={busy}
      />

      {isLoading ? (
        <LoadingWrapper message={`Loading ${memberColumnHeader} Device List`} />
      ) : loadError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void loadDevices(threshold)}>
              Retry
            </Button>
          }
        >
          {loadError}
        </Alert>
      ) : devices.length === 0 ? (
        <Alert severity="info">{threshold ? "No devices are stale by that threshold." : emptyMessage}</Alert>
      ) : (
        <DataGridPro
          apiRef={apiRef}
          getRowId={(row: DeviceGridRow) => row.id}
          sx={{ maxHeight: "calc(100vh - 300px)" }}
          density="compact"
          pagination
          checkboxSelection
          disableRowSelectionOnClick
          keepNonExistentRowsSelected={false}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={handleSelectionChange}
          treeData={grouped}
          // Always supplied, even when treeData is off. Making it conditional
          // throws "MUI X: No getTreeDataPath given" when grouping is switched
          // off, because the grid still has tree rows in flight on that render.
          getTreeDataPath={(row: DeviceGridRow) => row.treePath}
          groupingColDef={grouped ? groupingColDef : undefined}
          defaultGroupingExpansionDepth={0}
          // The Last Active cell stacks a relative and an absolute line.
          getRowHeight={() => 48}
          slots={{ toolbar: DeviceGridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              // Grouped mode filters owner rows only (see disableChildrenFiltering
              // below), so do not imply a device search that will not work there.
              placeholder: grouped
                ? `Search ${memberNoun}, emp. id, email`
                : `Search ${memberNoun}, emp. id, email, device`,
            },
          }}
          // Match only the owner rows when grouped, and keep every device beneath
          // a matching owner. Device rows carry no name/emp id/email of their own
          // - the group header holds those - so filtering them too leaves a
          // matching crew member with nothing inside when you expand them.
          disableChildrenFiltering={grouped}
          initialState={{ sorting: { sortModel: [{ field: "lastActive", sort: "asc" }] } }}
          columns={columns}
          rows={rows}
        />
      )}
    </>
  );
};

export default DeviceDataGrid;
